import { useState } from "react";
import { Badge } from "../../../shared/components/ui/badge";
import { normalizeCountryName } from "../../../utils/countryMapping";
import { normalizeLanguageNames } from "../../../utils/languageMapping";
import { useImageValidation } from "../hooks/useImageValidation";
import type { Submission, SubmissionAsset } from "../hooks/useSubmissions";
import { TitlePreview } from "../pages/ReviewStep/TitlePreview";
import { submissionToPreview } from "../utils/previewHelpers";
import { SubmissionImage } from "./SubmissionImage";
import { ValidationWarningIndicator } from "./ValidationWarningIndicator";

/* eslint-disable max-lines */
// NOTE: Multiple section components for submission display + preview

interface AssetListProps {
  assets: SubmissionAsset[];
}

function AssetList({ assets }: Readonly<AssetListProps>) {
  const getEncodingStatusVariant = (status: string): "default" | "secondary" | "destructive" => {
    if (status === "COMPLETED") return "secondary";
    if (status === "FAILED") return "destructive";
    return "default";
  };

  return (
    <div className="border-t border-neutral-800 pt-6">
      <h2 className="text-lg font-semibold text-white mb-4">Uploaded Assets</h2>
      <div className="space-y-2">
        {assets.map((asset) => (
          <div key={asset.id} className="bg-neutral-800/0 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-1">
              <span className="text-neutral-500 font-medium">{asset.kind}</span>
              <Badge variant={getEncodingStatusVariant(asset.encodingStatus)} className="text-xs">
                {asset.encodingStatus}
              </Badge>
            </div>
            {asset.rawFileName && <div className="text-sm text-white mt-1">File: {asset.rawFileName}</div>}
            {asset.contentType && <div className="text-xs text-neutral-400 mt-0.5">Type: {asset.contentType}</div>}
            {asset.uploadedBy && <div className="text-xs text-neutral-400 mt-0.5">Uploaded by: {asset.uploadedBy}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function BasicInfoSection({ submission }: { readonly submission: Submission }) {
  const releaseYear = submission.year || submission.releaseYear;
  const runtime = submission.durationMinutes || submission.runtime;

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">Basic Information</h2>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <p className="text-sm text-neutral-500 pb-2">Title</p>
          <p className="text-white text-sm">{submission.title}</p>
        </div>
        <div>
          <p className="text-sm text-neutral-500 pb-2">Release Year</p>
          <p className="text-white text-sm">{releaseYear || "N/A"}</p>
        </div>
        {runtime && (
          <div>
            <p className="text-sm text-neutral-500 pb-2">Runtime</p>
            <p className="text-white text-sm">{runtime} minutes</p>
          </div>
        )}
        {submission.tmdbId && (
          <div className="col-span-2">
            <p className="text-sm text-neutral-500 pb-2">TMDB ID</p>
            <p className="text-white text-sm">
              {submission.tmdbId}
              <a
                href={`https://www.themoviedb.org/movie/${submission.tmdbId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 text-lumi-accent hover:underline text-sm"
              >
                View on TMDB →
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function ContentDetailsSection({ submission }: { readonly submission: Submission }) {
  // Parse JSON fields - genreData is already an array of strings
  const genres = submission.genreData ? (submission.genreData as string[]) : submission.genres || [];

  // Normalize language codes to full names (e.g., "en" → "English")
  const languageNames =
    submission.languages && submission.languages.length > 0 ? normalizeLanguageNames(submission.languages) : [];

  // Normalize country code to full name (e.g., "IT" → "Italy")
  const countryName = submission.country ? normalizeCountryName(submission.country) : null;

  return (
    <div className="border-t border-neutral-800 pt-12">
      <h2 className="text-lg font-semibold text-white mb-4">Content Details</h2>
      <div className="space-y-10">
        <div className="grid grid-cols-4 gap-4">
          {submission.synopsis && (
            <div className="col-span-2 pr-20">
              <p className="text-sm text-neutral-500 pb-2">Synopsis</p>
              <p className="text-white text-sm">{submission.synopsis}</p>
            </div>
          )}
          {submission.description && (
            <div className="col-span-2">
              <p className="text-sm text-neutral-500 pb-2">Description</p>
              <p className="text-white text-sm whitespace-pre-wrap">{submission.description}</p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-4 gap-4">
          {genres && genres.length > 0 && (
            <div>
              <p className="text-sm text-neutral-500 pb-2">Genres</p>
              <p className="text-white text-sm">{genres.join(", ")}</p>
            </div>
          )}
          {submission.rating && (
            <div>
              <p className="text-sm text-neutral-500 pb-2">Content Rating</p>
              <p className="text-white text-sm">{submission.rating}</p>
            </div>
          )}
          {languageNames.length > 0 && (
            <div>
              <p className="text-sm text-neutral-500 pb-2">Languages</p>
              <p className="text-white text-sm">{languageNames.join(", ")}</p>
            </div>
          )}
          {countryName && (
            <div>
              <p className="text-sm text-neutral-500 pb-2">Country</p>
              <p className="text-white text-sm">{countryName}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function CreditsSection({ submission }: { readonly submission: Submission }) {
  // Parse JSON fields - directorData is array with one object, castData is array of objects
  const director = submission.directorData
    ? (submission.directorData as { name: string }[])[0]?.name
    : submission.director;

  const cast = submission.castData
    ? (submission.castData as { name: string }[]).map((c) => c.name)
    : submission.cast || [];

  // Parse crew data - array of {name, job, department?}
  const crew = submission.crewData ? (submission.crewData as { name: string; job: string; department?: string }[]) : [];

  if (!director && cast.length === 0 && crew.length === 0) return null;

  return (
    <div className="border-t border-neutral-800 pt-12">
      <h2 className="text-lg font-semibold text-white mb-4">Credits</h2>
      <div className="grid grid-cols-4 gap-8">
        {director && (
          <div className="col-span-1">
            <p className="text-sm text-neutral-500 pb-2">Director</p>
            <p className="text-white text-sm">{director}</p>
          </div>
        )}
        {cast.length > 0 && (
          <div className="col-span-1">
            <p className="text-sm text-neutral-500 pb-2">Cast</p>
            <div className="space-y-1">
              {cast.map((actor) => (
                <p key={actor} className="text-white text-sm">
                  {actor}
                </p>
              ))}
            </div>
          </div>
        )}
        {crew.length > 0 && (
          <div className="col-span-2">
            <p className="text-sm text-neutral-500 pb-2">Crew</p>
            <div className="space-y-1">
              {crew.map((member, idx) => (
                <div key={`${member.name}-${idx}`} className="flex items-baseline gap-2">
                  <span className="text-white text-sm">{member.name}</span>
                  <span className="text-neutral-400 text-xs">• {member.job}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line max-lines-per-function
export function VisualAssetsSection({
  submission,
  submissionId,
  titleId,
}: {
  readonly submission: Submission;
  readonly submissionId?: string | null;
  readonly titleId?: string | null;
}) {
  // Fetch validation warnings for title mode
  const { validationWarnings } = useImageValidation(titleId || null, titleId ? "title" : "submission");

  if (
    !submission.posterUrl &&
    !submission.backdropUrl &&
    !submission.logoUrl &&
    !submission.horizontalTileUrl &&
    !submission.verticalTileUrl
  )
    return null;

  return (
    <div className="border-t border-neutral-800 pt-12">
      <h2 className="text-lg font-semibold text-white mb-6">Visual Assets</h2>

      <div className="grid grid-cols-2 gap-6">
        {/* Left column: Backdrop, Logo, Tiles */}
        <div className="space-y-6">
          {/* Backdrop */}
          {submission.backdropUrl && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-neutral-500">Backdrop</p>
                <span className="text-xs text-neutral-600 bg-neutral-800 px-2 py-0.5 rounded">16:9</span>
              </div>
              <div className="relative">
                <SubmissionImage
                  src={submission.backdropUrl}
                  submissionId={submissionId}
                  titleId={titleId}
                  alt={`${submission.title} backdrop`}
                  className="w-full h-auto rounded-lg border border-neutral-700"
                />
                {validationWarnings.backdrop && validationWarnings.backdrop.length > 0 && (
                  <ValidationWarningIndicator warnings={validationWarnings.backdrop} />
                )}
              </div>
              <p className="text-xs text-neutral-600 mt-1">Used in title detail page</p>
            </div>
          )}

          {/* Logo */}
          {submission.logoUrl && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-neutral-500">Logo</p>
                <span className="text-xs text-neutral-600 bg-neutral-800 px-2 py-0.5 rounded">PNG</span>
              </div>
              <div className="relative">
                <SubmissionImage
                  src={submission.logoUrl}
                  submissionId={submissionId}
                  titleId={titleId}
                  alt={`${submission.title} logo`}
                  className="w-full h-auto rounded-lg border border-neutral-700 bg-neutral-800 p-4"
                />
                {validationWarnings.logo && validationWarnings.logo.length > 0 && (
                  <ValidationWarningIndicator warnings={validationWarnings.logo} />
                )}
              </div>
              <p className="text-xs text-neutral-600 mt-1">Overlays backdrop on detail page</p>
            </div>
          )}

          {/* Collection Tiles - nested 2-column grid */}
          {(submission.horizontalTileUrl || submission.verticalTileUrl) && (
            <div>
              <p className="text-sm text-neutral-400 mb-3">Collection Tiles</p>
              <div className="grid grid-cols-4 gap-4">
                {submission.verticalTileUrl && (
                  <div className="col-span-1">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-neutral-500">Vertical</p>
                      <span className="text-xs text-neutral-600 bg-neutral-800 px-1.5 py-0.5 rounded">2:3</span>
                    </div>
                    <div className="relative">
                      <SubmissionImage
                        src={submission.verticalTileUrl}
                        submissionId={submissionId}
                        titleId={titleId}
                        alt={`${submission.title} vertical tile`}
                        className="w-full h-auto rounded border border-neutral-700"
                      />
                      {validationWarnings["vertical-tile"] && validationWarnings["vertical-tile"].length > 0 && (
                        <ValidationWarningIndicator warnings={validationWarnings["vertical-tile"]} />
                      )}
                    </div>
                    <p className="text-xs text-neutral-600 mt-1">Grid layouts</p>
                  </div>
                )}
                {submission.horizontalTileUrl && (
                  <div className="col-span-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-neutral-500">Horizontal</p>
                      <span className="text-xs text-neutral-600 bg-neutral-800 px-1.5 py-0.5 rounded">16:9</span>
                    </div>
                    <div className="relative">
                      <SubmissionImage
                        src={submission.horizontalTileUrl}
                        submissionId={submissionId}
                        titleId={titleId}
                        alt={`${submission.title} horizontal tile`}
                        className="w-full h-auto rounded border border-neutral-700"
                      />
                      {validationWarnings["horizontal-tile"] && validationWarnings["horizontal-tile"].length > 0 && (
                        <ValidationWarningIndicator warnings={validationWarnings["horizontal-tile"]} />
                      )}
                    </div>
                    <p className="text-xs text-neutral-600 mt-1">Carousels</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Poster */}
        <div className="space-y-6">
          {submission.posterUrl && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-neutral-500">Poster</p>
                <span className="text-xs text-neutral-600 bg-neutral-800 px-2 py-0.5 rounded">2:3</span>
              </div>
              <div className="relative">
                <SubmissionImage
                  src={submission.posterUrl}
                  submissionId={submissionId}
                  titleId={titleId}
                  alt={`${submission.title} poster`}
                  className="w-full h-auto rounded-lg border border-neutral-700"
                />
                {validationWarnings.poster && validationWarnings.poster.length > 0 && (
                  <ValidationWarningIndicator warnings={validationWarnings.poster} />
                )}
              </div>
              <p className="text-xs text-neutral-600 mt-1">Fallback for missing tiles</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineItem({ label, date }: Readonly<{ label: string; date: string }>) {
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="flex items-center justify-between py-2 px-3 bg-neutral-800/0 rounded-lg">
      <span className="text-sm text-neutral-400 pb-2">{label}</span>
      <span className="text-white text-sm">{formatDate(date)}</span>
    </div>
  );
}

export function TimelineSection({ submission }: { readonly submission: Submission }) {
  return (
    <div className="border-t border-neutral-800 pt-12">
      <h2 className="text-lg font-semibold text-white mb-4">Submission Timeline</h2>
      <div className="space-y-3">
        <TimelineItem label="Created" date={submission.createdAt} />
        {submission.submittedAt && <TimelineItem label="Submitted for Review" date={submission.submittedAt} />}
        {submission.reviewedAt && <TimelineItem label="Reviewed" date={submission.reviewedAt} />}
        {submission.publishedAt && <TimelineItem label="Published" date={submission.publishedAt} />}
        <TimelineItem label="Last Updated" date={submission.updatedAt} />
      </div>
    </div>
  );
}

export function ReviewNotesSection({ submission }: Readonly<{ submission: Submission }>) {
  if (!submission.reviewNotes) return null;

  return (
    <div className="border-t border-neutral-800 pt-6">
      <h2 className="text-lg font-semibold text-white mb-4">Admin Review Notes</h2>
      <div className="bg-neutral-800 rounded-lg p-4">
        <p className="text-white whitespace-pre-wrap">{submission.reviewNotes}</p>
      </div>
    </div>
  );
}

export function SubmissionContent({ submission }: Readonly<{ submission: Submission }>) {
  const [viewMode, setViewMode] = useState<"data" | "preview">("data");
  const previewData = submissionToPreview(submission as unknown as Parameters<typeof submissionToPreview>[0]);

  return (
    <div className="bg-neutral-900/0 border border-neutral-800/0 p-2 space-y-12">
      {/* View Mode Toggle */}
      <div className="flex items-center gap-2 p-1 bg-neutral-900 border border-neutral-800 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => setViewMode("data")}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
            viewMode === "data" ? "bg-neutral-800 text-white" : "text-neutral-400 hover:text-white"
          }`}
        >
          Details
        </button>
        <button
          type="button"
          onClick={() => setViewMode("preview")}
          className={`px-4 py-2 text-sm font-medium rounded transition-colors ${
            viewMode === "preview" ? "bg-neutral-800 text-white" : "text-neutral-400 hover:text-white"
          }`}
        >
          Preview
        </button>
      </div>

      {/* Preview Mode */}
      {viewMode === "preview" && (
        <div>
          <TitlePreview data={previewData} submissionId={submission.id} />
        </div>
      )}

      {/* Data View Mode */}
      {viewMode === "data" && (
        <>
          <BasicInfoSection submission={submission} />
          <ContentDetailsSection submission={submission} />
          <CreditsSection submission={submission} />
          <VisualAssetsSection submission={submission} submissionId={submission.id} titleId={null} />

          {/* Assets */}
          {submission.assets && submission.assets.length > 0 && <AssetList assets={submission.assets} />}

          <TimelineSection submission={submission} />
          <ReviewNotesSection submission={submission} />
        </>
      )}
    </div>
  );
}
